import React, { useMemo } from 'react';
import { Bar, Doughnut, Line } from 'react-chartjs-2';
import { ensureChartJsRegistered } from './registerChartJs';

// Single chart wrapper used by ReportMessage. The server already emits
// a fully-formed Chart.js config (`{type, data, options, title}`); the
// wrapper picks the right react-chartjs-2 element by type and renders
// it inside a fixed-height box so the chart respects flex layout.
//
// The config's colors are re-themed here to the Organic design system.
// Canvas can't resolve CSS custom properties, so the hex values below
// mirror the tokens in styles/organic.css (accent / accent-2 ramps,
// divider, text, bg) — bars in terracotta/sage, grid on the divider
// tone, category labels in Figtree, numeric values in Caprasimo.
const PALETTE = [
  '#c67139', // --color-accent
  '#7a8a5e', // --color-accent-2
  '#f6a06b', // --color-accent-400
  '#aebf92', // --color-accent-2-400
  '#8c491a', // --color-accent-700
  '#56633f', // --color-accent-2-700
];
const GRID = 'rgba(32, 30, 29, 0.12)';   // ~ --color-divider
const BORDER = 'rgba(32, 30, 29, 0.16)'; // --color-divider
const TEXT = '#201e1d';                  // --color-text
const MUTED = 'rgba(32, 30, 29, 0.7)';
const BG = '#f5ead8';                    // --color-bg
const FONT_BODY = "'Figtree', system-ui, sans-serif";
const FONT_HEADING = "'Caprasimo', system-ui, sans-serif";

function themeData(data, type) {
  const datasets = (data.datasets || []).map((ds, i) => {
    const color = PALETTE[i % PALETTE.length];
    if (type === 'doughnut') {
      const n = (ds.data || []).length || PALETTE.length;
      return {
        ...ds,
        backgroundColor: Array.from({ length: n }, (_, j) => PALETTE[j % PALETTE.length]),
        borderColor: BG,
        borderWidth: 2,
      };
    }
    if (type === 'line') {
      return {
        ...ds,
        borderColor: color,
        backgroundColor: color,
        pointBackgroundColor: color,
        pointBorderColor: color,
      };
    }
    return { ...ds, backgroundColor: color, borderColor: color, borderRadius: 6, borderWidth: 0 };
  });
  return { ...data, datasets };
}

function themeOptions(options, type) {
  const o = { ...options };

  const plugins = { ...(o.plugins || {}) };
  plugins.legend = {
    ...(plugins.legend || {}),
    labels: {
      ...((plugins.legend || {}).labels || {}),
      color: TEXT,
      font: { family: FONT_BODY, size: 12 },
    },
  };
  plugins.title = {
    ...(plugins.title || {}),
    color: TEXT,
    font: { family: FONT_HEADING, size: 14, weight: 400 },
  };
  plugins.tooltip = {
    ...(plugins.tooltip || {}),
    backgroundColor: '#2e2b25',
    titleColor: '#f9f4ed',
    bodyColor: '#f9f4ed',
    titleFont: { family: FONT_BODY },
    bodyFont: { family: FONT_BODY },
  };
  o.plugins = plugins;

  if (type !== 'doughnut') {
    // Numeric (value) axis ticks get Caprasimo; category labels stay in
    // Figtree. Horizontal bars flip which axis is numeric.
    const valueAxis = o.indexAxis === 'y' ? 'x' : 'y';
    const scales = { ...(o.scales || {}) };
    ['x', 'y'].forEach((axis) => {
      const s = { ...(scales[axis] || {}) };
      s.grid = { ...(s.grid || {}), color: GRID };
      s.border = { ...(s.border || {}), color: BORDER };
      s.ticks = {
        ...(s.ticks || {}),
        color: axis === valueAxis ? TEXT : MUTED,
        font: axis === valueAxis
          ? { family: FONT_HEADING, size: 11, weight: 400 }
          : { family: FONT_BODY, size: 12 },
      };
      scales[axis] = s;
    });
    o.scales = scales;
  }

  return o;
}

function ReportChart({ config }) {
  ensureChartJsRegistered();
  // Defensive clone — react-chartjs-2 mutates the options object when
  // it resolves defaults, which can re-trigger renders if we hand it
  // the same reference each time. The clone is then re-themed to the
  // Organic palette.
  const data = useMemo(
    () => themeData(JSON.parse(JSON.stringify(config.data || {})), config.type),
    [config],
  );
  const options = useMemo(
    () => themeOptions(JSON.parse(JSON.stringify(config.options || {})), config.type),
    [config],
  );

  let ChartTag;
  switch (config.type) {
    case 'doughnut': ChartTag = Doughnut; break;
    case 'line': ChartTag = Line; break;
    case 'bar':
    default:
      ChartTag = Bar;
  }

  return (
    <figure className="chat-report-chart">
      {config.title ? <figcaption>{config.title}</figcaption> : null}
      <div className="chat-report-chart-canvas">
        <ChartTag data={data} options={options} />
      </div>
    </figure>
  );
}

export default ReportChart;
